import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { addDays, isWallTime } from '@kraftwerk/shared';
import type { IsoDate, TrainingOccurrence } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { moveOccurrence, upcomingTraining } from '../../db/schedule.js';
import { formatDay } from '../../format.js';
import { useTimeZone } from '../../hooks/useToday.js';

/**
 * Einen Termin verschieben (18.10.2026): die nächsten drei **freien** Tage
 * als ein Tipp zur gewohnten Zeit — heute nur fürs Nachholen und nur, solange
 * die Uhrzeit noch vor einem liegt; ein Tag, an dem schon trainiert wird,
 * kommt nicht in Frage —, sonst Tag und Uhrzeit frei. Der Plan-Tag wandert mit, weil er rotiert:
 * Der nächste offene Termin bekommt den wartenden.
 */
export function MoveTraining({
  entry,
  today,
  onDone,
}: {
  entry: TrainingOccurrence;
  today: string;
  onDone: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const busy = useLiveQuery(
    async () =>
      timeZone
        ? new Set(
            (await upcomingTraining(timeZone, 10, db))
              .filter((item) => item.status === 'planned' && !item.missed)
              .map((item) => item.date),
          )
        : undefined,
    [timeZone],
  );
  const [other, setOther] = useState(false);
  const [date, setDate] = useState(addDays(today as IsoDate, 1));
  const [time, setTime] = useState(entry.time);
  const ids = { date: useId(), time: useId() };
  const move = (target: string, at: string) =>
    void moveOccurrence(entry, target, at, db).then(onDone);

  const now = new Date().toTimeString().slice(0, 5);
  // Erst wenn bekannt ist, wo schon trainiert wird — sonst stünde kurz ein
  // belegter Tag zur Wahl (im Test gefunden).
  const quick = (
    busy ? Array.from({ length: 7 }, (_, index) => addDays(today as IsoDate, index)) : []
  )
    .filter((date) => date !== entry.date && !busy?.has(date))
    .filter((date) => date !== today || (entry.date < today && entry.time > now))
    .slice(0, 3)
    .map((date) => ({
      date,
      label:
        date === today
          ? t('schedule.quick.today', { time: entry.time })
          : date === addDays(today as IsoDate, 1)
            ? t('schedule.quick.tomorrow', { time: entry.time })
            : t('schedule.quick.day', {
                day: formatDay(date, 'weekdayShort', i18n.language),
                time: entry.time,
              }),
    }));

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-line bg-surface-raised p-3">
      <div className="flex flex-wrap gap-2">
        {quick.map((option) => (
          <button
            key={option.date}
            type="button"
            onClick={() => move(option.date, entry.time)}
            className="kw-btn kw-btn-small flex-1 px-3"
          >
            {option.label}
          </button>
        ))}
        <button
          type="button"
          aria-expanded={other}
          onClick={() => setOther((value) => !value)}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1 px-3"
        >
          {t('schedule.quick.other')}
        </button>
      </div>
      {other ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!isWallTime(time) || date === '') return;
            move(date, time);
          }}
        >
          <label htmlFor={ids.date} className="flex w-40 flex-col gap-1">
            <span className="kw-label">{t('schedule.date')}</span>
            <input
              id={ids.date}
              type="date"
              value={date}
              min={today}
              onChange={(event) => setDate(event.target.value)}
              className="kw-field"
            />
          </label>
          <label htmlFor={ids.time} className="flex w-28 flex-col gap-1">
            <span className="kw-label">{t('schedule.time')}</span>
            <input
              id={ids.time}
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              className="kw-field"
            />
          </label>
          <button type="submit" className="kw-btn kw-btn-small px-3">
            {t('schedule.move')}
          </button>
        </form>
      ) : null}
    </div>
  );
}
